import { PayloadAction, createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { ApiState } from '../constants/enums';
import rateFinderServices from '../services/rateFinderServices';
import {
    IClassVariation,
    IClassVariationsResponse,
    IClientLineOfBusinessesResponse,
    IClientLob,
    IClientLobVariationsResponse,
    ICoreTax,
    ICoreTaxResponse,
    ICoreTaxesRequest,
    ILobCoreTaxesRequest,
    ICountries,
    ICountriesRepsonse,
    ILocationData,
    IRegionsRepsonse,
    ISelectedVariations,
    ISubRegionsRepsonse,
    ITaxResponse,
    InsuranceClass,
    InsuranceClassesResponse,
    InsurerStatus,
    IUnderWrittingStatusResponse,
    IUnderWrittingStatusRequest,
    IClientInsurer,
    IExportCoreTaxesRequest,
} from '../types/rate.type';
import _ from 'lodash';

export const initialLocationData = {
    selectedCountry: '',
    selectedRegion: '',
    selectedSubRegion: '',
    selectedCountryName: null,
    selectedRegionName: null,
    selectedSubRegionName: null,
    regions: [],
    subRegions: [],
    variations: [],
    insurerStatus: {
        admitted: true,
        nonAdmitted: false,
    },
    selectedVariations: [],
    selectAllIndex: [],
    isOpen: true,
    lobVariations: [],
    isNewlyAddedRiskLocationCountry: false,
    selectAllLobIds: [],
    isDeleted: false,
    isDisabled: false,
    isEEA: false,
    isGermanEEA: null,
    isGermanLocalPolicy: false,
};

export type UpdatePayloadLocalPolicy = {
    index: number;
    code: string;
    isDisabled: boolean;
};

export type UpdatePayload = {
    index: number;
    code: string;
};

export type CountryPayload = {
    index: number;
    countrycode: string;
    countryname: string;
};

export type RiskLocationDetailsPayload = {
    index: number;
    selectedCountryCode: string;
    selectedRegionCode: string;
    selectedSubRegionCode: string;
};

export type ClientSubLobs = {
    clientLob: string;
    subLobs: string[];
};

export interface RateFinderState {
    apiState: {
        getCountries: ApiState;
        getRegions: ApiState;
        getSubRegions: ApiState;
        getLocalPolicyCountries: ApiState;
        getLocalPolicyRegions: ApiState;
        getLocalPolicySubRegions: ApiState;
        getCoreTaxes: ApiState;
        getLobCoreTaxes: ApiState;
        getUnderwritingStatus: ApiState;
        exportCoreTaxes: ApiState;
        getCoreTaxById: ApiState;
        getClassVariations: ApiState;
        getClientLobs: ApiState;
        getClientLobVariations: ApiState;
        getInsuranceClasses: ApiState;
    };
    countries: ICountries[];
    localPolicyCountries: ICountries[];
    locationData: ILocationData[];
    localPolicyLocations: ILocationData[];
    insuranceClasses: InsuranceClass[];
    coreTaxes: ICoreTax[];
    pagination: {
        totalElements: number;
        totalPages: string;
        pageNumber: number;
        pageSize: number;
    };
    allTaxes: ITaxResponse[];
    insClassIds: string[];
    clientLobs: IClientLob[];
    selectedClientLobs: string[];
    selectedSubLobs: ClientSubLobs[];
    insurerUnderWritingStatus: IClientInsurer[];
    effectiveDate?: Date;
}

export const initialState: RateFinderState = {
    apiState: {
        getCountries: ApiState.NOT_STARTED,
        getRegions: ApiState.NOT_STARTED,
        getSubRegions: ApiState.NOT_STARTED,
        getLocalPolicyCountries: ApiState.NOT_STARTED,
        getLocalPolicyRegions: ApiState.NOT_STARTED,
        getLocalPolicySubRegions: ApiState.NOT_STARTED,
        getCoreTaxes: ApiState.NOT_STARTED,
        getLobCoreTaxes: ApiState.NOT_STARTED,
        getUnderwritingStatus: ApiState.NOT_STARTED,
        exportCoreTaxes: ApiState.NOT_STARTED,
        getCoreTaxById: ApiState.NOT_STARTED,
        getClassVariations: ApiState.NOT_STARTED,
        getClientLobs: ApiState.NOT_STARTED,
        getClientLobVariations: ApiState.NOT_STARTED,
        getInsuranceClasses: ApiState.NOT_STARTED,
    },
    countries: [],
    localPolicyCountries: [],
    locationData: [
        {
            ...initialLocationData,
        },
    ],
    localPolicyLocations: [],
    insuranceClasses: [],
    coreTaxes: [],
    pagination: {
        totalElements: 0,
        totalPages: '',
        pageNumber: 0,
        pageSize: 0,
    },
    allTaxes: [],
    insClassIds: [],
    clientLobs: [],
    selectedClientLobs: [],
    selectedSubLobs: [],
    insurerUnderWritingStatus: [],
    effectiveDate: new Date(),
};

export const getCountries = createAsyncThunk<ICountriesRepsonse>('rate-finder/get-countries', async () => {
    return rateFinderServices.getCountries().then((res) => res);
});

export const getRegions = createAsyncThunk<IRegionsRepsonse, { index: number; code: string }>(
    'rate-finder/get-regions',
    async ({ code }) => {
        return rateFinderServices.getRegions([code]).then((res) => res);
    },
);

export const getSubRegions = createAsyncThunk<ISubRegionsRepsonse, { index: number; code: string }>(
    'rate-finder/get-sub-regions',
    async ({ code }) => {
        return rateFinderServices.getSubRegions([code]).then((res) => res);
    },
);

export const getLocalPolicyCountries = createAsyncThunk<ICountriesRepsonse>('rate-finder/get-localpolicy-countries', async () => {
    return rateFinderServices.getCountries().then((res) => res);
});

export const getLocalPolicyRegions = createAsyncThunk<IRegionsRepsonse, { index: number; code: string }>(
    'rate-finder/get-localpolicy-regions',
    async ({ code }) => {
        return rateFinderServices.getRegions([code]).then((res) => res);
    },
);

export const getLocalPolicySubRegions = createAsyncThunk<ISubRegionsRepsonse, { index: number; code: string }>(
    'rate-finder/get-localpolicy-subregions',
    async ({ code }) => {
        return rateFinderServices.getSubRegions([code]).then((res) => res);
    },
);

export const getInsuranceClasses = createAsyncThunk<InsuranceClassesResponse>(
    'rate-finder/get-insurance-classes',
    async () => {
        return rateFinderServices.getInsuranceClasses().then((res) => res);
    },
);

export const getClassVariations = createAsyncThunk<
    IClassVariationsResponse,
    { index: number; code: string; effectiveDate: Date | string; regionId?: string; subRegionId?: string }
>('rate-finder/get-class-variations', async ({ code, effectiveDate, regionId, subRegionId }) => {
    return rateFinderServices.getClassVariations(code, effectiveDate, regionId, subRegionId).then((res) => res);
});

export const getCoreTaxes = createAsyncThunk<ICoreTaxResponse, ICoreTaxesRequest>(
    'rate-finder/get-core-taxes',
    async (taxPayload: ICoreTaxesRequest) => {
        return rateFinderServices.getCoreTaxes(taxPayload).then((res) => res);
    },
);

export const getLobCoreTaxes = createAsyncThunk<ICoreTaxResponse, ILobCoreTaxesRequest>(
    'rate-finder/get-lob-core-taxes',
    async (taxPayload: ILobCoreTaxesRequest) => {
        return rateFinderServices.getLobCoreTaxes(taxPayload).then((res) => res);
    },
);

export const getUnderwritingStatus = createAsyncThunk<IUnderWrittingStatusResponse, IUnderWrittingStatusRequest>(
    'rate-finder/get-underwriting-status',
    async (statusPayload: IUnderWrittingStatusRequest) => {
        return rateFinderServices.getUnderwritingStatus(statusPayload).then((res) => res);
    },
);

export const exportCoreTaxes = createAsyncThunk<any, IExportCoreTaxesRequest>(
    'rate-finder/export-core-taxes',
    async (taxPayload: IExportCoreTaxesRequest) => {
        return rateFinderServices.exportCoreTaxes(taxPayload).then((res) => res);
    },
);

export const getCoreTaxById = createAsyncThunk<ITaxResponse, { taxId: string }>(
    'rate-finder/get-core-tax-by-id',
    async ({ taxId }) => {
        return rateFinderServices.getCoreTaxById(taxId).then((res) => res);
    },
);

export const getClientLobs = createAsyncThunk<IClientLineOfBusinessesResponse>(
    'rate-finder/get-client-lob',
    async () => {
        return rateFinderServices.getClientLineOfBusinesses().then((res) => res);
    },
);

export const getClientLobVariations = createAsyncThunk<
    IClientLobVariationsResponse,
    { index: number; countryCode: string; effectiveDate: Date | string; regionId?: string; subRegionId?: string }
>('rate-finder/get-lob-variations', async ({ countryCode, effectiveDate, regionId, subRegionId }) => {
    return rateFinderServices
        .getClientLobVariations(countryCode, effectiveDate, regionId, subRegionId)
        .then((res) => res);
});

const rateFinderSlice = createSlice({
    name: 'rate-finder',
    initialState,
    extraReducers(builder) {
        builder
            .addCase(getCountries.pending, (state) => {
                state.apiState.getCountries = ApiState.PENDING;
            })
            .addCase(getCountries.fulfilled, (state, action: PayloadAction<ICountriesRepsonse>) => {
                state.countries = action.payload.data.countries;
                state.apiState.getCountries = ApiState.FULFILLED;
            })
            .addCase(getCountries.rejected, (state) => {
                state.apiState.getCountries = ApiState.REJECTED;
            })
            .addCase(getLocalPolicyCountries.pending, (state) => {
                state.apiState.getLocalPolicyCountries = ApiState.PENDING;
            })
            .addCase(getLocalPolicyCountries.fulfilled, (state, action: PayloadAction<ICountriesRepsonse>) => {
                state.localPolicyCountries = action.payload.data.countries;
                state.apiState.getLocalPolicyCountries = ApiState.FULFILLED;
            })
            .addCase(getLocalPolicyCountries.rejected, (state) => {
                state.apiState.getLocalPolicyCountries = ApiState.REJECTED;
            })
            .addCase(getRegions.pending, (state) => {
                state.apiState.getRegions = ApiState.PENDING;
            })
            .addCase(getRegions.fulfilled, (state, action) => {
                const { index } = action.meta.arg;
                if (state.locationData[index])
                    state.locationData[index].regions = action.payload.data.countries[0].regions;
                state.apiState.getRegions = ApiState.FULFILLED;
            })
            .addCase(getRegions.rejected, (state, action) => {
                const { index } = action.meta.arg;
                if (state.locationData[index]) state.locationData[index].regions = [];
                state.apiState.getRegions = ApiState.REJECTED;
            })
            .addCase(getLocalPolicyRegions.pending, (state) => {
                state.apiState.getLocalPolicyRegions = ApiState.PENDING;
            })
            .addCase(getLocalPolicyRegions.fulfilled, (state, action) => {
                const { index } = action.meta.arg;
                if (state.localPolicyLocations[index])
                    state.localPolicyLocations[index].regions = action.payload.data.countries[0].regions;
                state.apiState.getLocalPolicyRegions = ApiState.FULFILLED;
            })
            .addCase(getLocalPolicyRegions.rejected, (state, action) => {
                const { index } = action.meta.arg;
                if (state.localPolicyLocations[index]) state.localPolicyLocations[index].regions = [];
                state.apiState.getLocalPolicyRegions = ApiState.REJECTED;
            })
            .addCase(getSubRegions.pending, (state) => {
                state.apiState.getSubRegions = ApiState.PENDING;
            })
            .addCase(getSubRegions.fulfilled, (state, action) => {
                const { index } = action.meta.arg;
                if (state.locationData[index])
                    state.locationData[index].subRegions = action.payload.data.regions[0].subRegions;
                state.apiState.getSubRegions = ApiState.FULFILLED;
            })
            .addCase(getSubRegions.rejected, (state, action) => {
                const { index } = action.meta.arg;
                if (state.locationData[index]) state.locationData[index].subRegions = [];
                state.apiState.getSubRegions = ApiState.REJECTED;
            })
            .addCase(getLocalPolicySubRegions.pending, (state) => {
                state.apiState.getLocalPolicySubRegions = ApiState.PENDING;
            })
            .addCase(getLocalPolicySubRegions.fulfilled, (state, action) => {
                const { index } = action.meta.arg;
                if (state.localPolicyLocations[index])
                    state.localPolicyLocations[index].subRegions = action.payload.data.regions[0].subRegions;
                state.apiState.getLocalPolicySubRegions = ApiState.FULFILLED;
            })
            .addCase(getLocalPolicySubRegions.rejected, (state, action) => {
                const { index } = action.meta.arg;
                if (state.localPolicyLocations[index]) state.localPolicyLocations[index].subRegions = [];
                state.apiState.getLocalPolicySubRegions = ApiState.REJECTED;
            })
            .addCase(getInsuranceClasses.fulfilled, (state, action: PayloadAction<InsuranceClassesResponse>) => {
                state.insuranceClasses = action.payload.data.insuranceClasses;
                state.apiState.getInsuranceClasses = ApiState.FULFILLED;
            })
            .addCase(getInsuranceClasses.rejected, (state) => {
                state.insuranceClasses = [];
                state.apiState.getInsuranceClasses = ApiState.REJECTED;
            })
            .addCase(getInsuranceClasses.pending, (state) => {
                state.apiState.getInsuranceClasses = ApiState.PENDING;
            })
            .addCase(getClassVariations.pending, (state) => {
                state.apiState.getClassVariations = ApiState.PENDING;
            })
            .addCase(getClassVariations.rejected, (state, action) => {
                const { index } = action.meta.arg;
                if (state.locationData[index]) state.locationData[index].variations = [];
                state.apiState.getClassVariations = ApiState.REJECTED;
            })
            .addCase(getClassVariations.fulfilled, (state, action) => {
                const { index } = action.meta.arg;
                if (state.locationData[index]) state.locationData[index].variations = action.payload.data.jurisdictions;
                state.apiState.getClassVariations = ApiState.FULFILLED;
            })
            .addCase(getCoreTaxes.pending, (state) => {
                state.apiState.getCoreTaxes = ApiState.PENDING;
            })
            .addCase(getCoreTaxes.fulfilled, (state, action: PayloadAction<ICoreTaxResponse>) => {
                const paginationData = {
                    totalElements: action.payload.data.totalElements,
                    totalPages: action.payload.data.totalPages,
                    pageNumber: action.payload.data.pageNumber,
                    pageSize: action.payload.data.pageSize,
                };
                state.coreTaxes = action.payload.data.coreTaxes;
                state.pagination = paginationData;
                state.apiState.getCoreTaxes = ApiState.FULFILLED;
            })
            .addCase(getCoreTaxes.rejected, (state) => {
                state.apiState.getCoreTaxes = ApiState.REJECTED;
            })
            .addCase(getLobCoreTaxes.pending, (state) => {
                state.apiState.getLobCoreTaxes = ApiState.PENDING;
            })
            .addCase(getLobCoreTaxes.fulfilled, (state, action: PayloadAction<ICoreTaxResponse>) => {
                const paginationData = {
                    totalElements: action.payload.data.totalElements,
                    totalPages: action.payload.data.totalPages,
                    pageNumber: action.payload.data.pageNumber,
                    pageSize: action.payload.data.pageSize,
                };
                state.coreTaxes = action.payload.data.coreTaxes;
                state.pagination = paginationData;
                state.apiState.getLobCoreTaxes = ApiState.FULFILLED;
            })
            .addCase(getLobCoreTaxes.rejected, (state) => {
                state.apiState.getLobCoreTaxes = ApiState.REJECTED;
            })
            .addCase(getUnderwritingStatus.pending, (state) => {
                state.apiState.getUnderwritingStatus = ApiState.PENDING;
            })
            .addCase(getUnderwritingStatus.fulfilled, (state, action: PayloadAction<IUnderWrittingStatusResponse>) => {
                state.insurerUnderWritingStatus = action.payload.data.clientInsurers;
                state.apiState.getUnderwritingStatus = ApiState.FULFILLED;
            })
            .addCase(getUnderwritingStatus.rejected, (state) => {
                state.apiState.getUnderwritingStatus     = ApiState.REJECTED;
            })
            .addCase(getClientLobs.pending, (state) => {
                state.apiState.getClientLobs = ApiState.PENDING;
            })
            .addCase(getClientLobs.fulfilled, (state, action: PayloadAction<IClientLineOfBusinessesResponse>) => {
                state.clientLobs = action.payload.data.clientLobs;
                state.apiState.getClientLobs = ApiState.FULFILLED;
            })
            .addCase(getClientLobs.rejected, (state) => {
                state.apiState.getClientLobs = ApiState.REJECTED;
            })
            .addCase(getClientLobVariations.pending, (state) => {
                state.apiState.getClientLobVariations = ApiState.PENDING;
            })
            .addCase(getClientLobVariations.fulfilled, (state, action) => {
                const { index } = action.meta.arg;
                if (state.locationData[index]) state.locationData[index].lobVariations = action.payload.data.clientLobs;
                state.apiState.getClientLobVariations = ApiState.FULFILLED;
            })
            .addCase(getClientLobVariations.rejected, (state) => {
                state.apiState.getClientLobVariations = ApiState.REJECTED;
            });
    },
    reducers: {
        addLocationData(state) {
            state.coreTaxes = [];
            state.locationData = [...state.locationData, initialLocationData];
        },
        addLocalPolicyLocationData(state, action: PayloadAction<{ isDisabled?: boolean }>) {
            let intiialData = { ...initialLocationData };
            intiialData.isDisabled = action.payload.isDisabled || false;
            state.localPolicyLocations = [...state.localPolicyLocations, intiialData];
        },

        // addLocationDataRiskLocation(state, action: PayloadAction<RiskLocationDetailsPayload>) {
        //     state.locationData = [
        //         ...state.locationData,
        //         {
        //             selectedCountry: action.payload.selectedCountryCode,
        //             selectedRegion: action.payload.selectedRegionCode,
        //             selectedSubRegion: action.payload.selectedSubRegionCode,
        //         },
        //     ];
        // },

        addLocationDataAddCountry(state, action: PayloadAction<CountryPayload>) {
            const index = state.locationData.findIndex((item) => item.selectedCountry === '');
            if (index !== -1) {
                state.locationData[index].selectedCountry = action.payload.countrycode;
                state.locationData[index].selectedCountryName =
                    state.countries.find((item) => item.countryName === action.payload.countryname)?.countryName || '';
            }
        },

        removeLastLocationData(state) {
            let emptyElements = state.locationData.filter((item) => item.selectedCountry === '').length;
            if (emptyElements > 0) state.locationData = [...state.locationData.slice(0, -1 * emptyElements)];
        },

        removeLocalPolicyLastLocationData(state) {
            let emptyElements = state.localPolicyLocations.filter((item) => item.selectedCountry === '').length;
            if (emptyElements > 0) state.localPolicyLocations = [...state.localPolicyLocations.slice(0, -1 * emptyElements)];
        },
        resetLocalPolicyLocationData(state) {
            state.localPolicyLocations = [];
        },
        removeLocationData(state, action: PayloadAction<number>) {
            // Remove the specified location by index
            state.locationData = state.locationData.filter((_, index) => index !== action.payload);

            // Reset related states if all locations are unselected
            const allLocationsUnselected = state.locationData.every((location) => location.selectedCountry === '');
            if (allLocationsUnselected) {
                state.selectedClientLobs = [];
                state.selectedSubLobs = [];
            }            

            // Clear core taxes
            state.coreTaxes = [];
        },
        setNewlyAddedRiskLocationCountry(state, action: PayloadAction<{ index: number; value: boolean }>) {
            const { index, value } = action.payload;
            state.locationData[index].isNewlyAddedRiskLocationCountry = value;
        },
        setisGermanEEA(state, action: PayloadAction<{ index: number; value: boolean }>) {
            const { index, value } = action.payload;
            state.locationData[index].isGermanEEA = value;
        },
        setisGermanLocalPolicy(state, action: PayloadAction<{ index: number; value: boolean }>) {
            const { index, value } = action.payload;
            state.locationData[index].isGermanLocalPolicy = value;
        },
        setSelectedCountry(state, action: PayloadAction<UpdatePayload>) {
            const index = action.payload.index;
            state.locationData[index].selectedCountry = action.payload.code;
            state.locationData[index].selectedCountryName =
            state.countries.find((item) => item.countryCode === action.payload.code)?.countryName || '';
            state.locationData[index].isEEA = state.countries.find((item) => item.countryCode === action.payload.code)?.isEEA || false;
            // state.locationData[index].isGermanEEA = null;
            state.locationData[index].selectedRegion = '';
            state.locationData[index].selectedSubRegion = '';
            state.locationData[index].selectedSubRegionName = null;
            state.locationData[index].selectedSubRegionName = null;
            state.locationData[index].regions = [];
            state.locationData[index].subRegions = [];
            state.locationData[index].variations = [];
            state.locationData[index].selectedVariations = [];
            state.locationData[index].selectAllIndex = [];
            state.locationData[index].insurerStatus = {
                admitted: true,
                nonAdmitted: false,
            };
            state.coreTaxes = [];
        },

        setLocalPolicySelectedCountry(state, action: PayloadAction<UpdatePayloadLocalPolicy>) {
            const index = action.payload.index;
            state.localPolicyLocations[index].selectedCountry = action.payload.code;
            state.localPolicyLocations[index].isDisabled = action.payload.isDisabled;
            state.localPolicyLocations[index].selectedCountryName =
                state.countries.find((item) => item.countryCode === action.payload.code)?.countryName || '';
            state.localPolicyLocations[index].selectedRegion = '';
            state.localPolicyLocations[index].selectedSubRegion = '';
            state.localPolicyLocations[index].selectedSubRegionName = null;
            state.localPolicyLocations[index].regions = [];
            state.localPolicyLocations[index].subRegions = [];
            state.localPolicyLocations[index].variations = [];
            state.localPolicyLocations[index].selectedVariations = [];
            state.localPolicyLocations[index].selectAllIndex = [];
            state.localPolicyLocations[index].insurerStatus = {
                admitted: true,
                nonAdmitted: false,
            };
            state.coreTaxes = [];
        },

        setSelectedRegion(state, action: PayloadAction<UpdatePayload>) {
            const index = action.payload.index;
            state.locationData[index].selectedRegion = action.payload.code;
            state.locationData[index].selectedRegionName =
                state.locationData[index].regions.find((item) => item.regionId === action.payload.code)?.regionName ||
                '';
            state.locationData[index].subRegions = [];
            state.locationData[index].variations = [];
            state.locationData[index].selectedVariations = [];
            state.locationData[index].selectAllIndex = [];
            state.locationData[index].insurerStatus = {
                admitted: true,
                nonAdmitted: false,
            };
            state.coreTaxes = [];
            state.locationData[index].selectedSubRegionName = null;
        },

        setLocalPolicySelectedRegion(state, action: PayloadAction<UpdatePayloadLocalPolicy>) {
            const index = action.payload.index;
            state.localPolicyLocations[index].selectedRegion = action.payload.code;
            state.localPolicyLocations[index].selectedRegionName =
                state.localPolicyLocations[index].regions.find((item) => item.regionId === action.payload.code)?.regionName ||
                '';
            state.localPolicyLocations[index].subRegions = [];
            state.localPolicyLocations[index].variations = [];
            state.localPolicyLocations[index].selectedVariations = [];
            state.localPolicyLocations[index].selectAllIndex = [];
            state.localPolicyLocations[index].insurerStatus = {
                admitted: true,
                nonAdmitted: false,
            };
            state.coreTaxes = [];
            state.localPolicyLocations[index].selectedSubRegionName = null;
        },
        setSelectedSubRegion(state, action: PayloadAction<UpdatePayload>) {
            const index = action.payload.index;
            state.locationData[index].selectedSubRegion = action.payload.code;
            state.locationData[index].selectedSubRegionName =
                state.locationData[index].subRegions.find((item) => item.subRegionId === action.payload.code)
                    ?.subRegionName || '';
            state.locationData[index].variations = [];
            state.locationData[index].selectedVariations = [];
            state.locationData[index].selectAllIndex = [];
            state.locationData[index].insurerStatus = {
                admitted: true,
                nonAdmitted: false,
            };
            state.coreTaxes = [];
        },

        setLocalPolicySelectedSubRegion(state, action: PayloadAction<UpdatePayload>) {
            const index = action.payload.index;
            state.localPolicyLocations[index].selectedSubRegion = action.payload.code;
            state.localPolicyLocations[index].selectedSubRegionName =
                state.localPolicyLocations[index].subRegions.find((item) => item.subRegionId === action.payload.code)
                    ?.subRegionName || '';
            state.localPolicyLocations[index].variations = [];
            state.localPolicyLocations[index].selectedVariations = [];
            state.localPolicyLocations[index].selectAllIndex = [];
            state.localPolicyLocations[index].insurerStatus = {
                admitted: true,
                nonAdmitted: false,
            };
            state.coreTaxes = [];
        },

        resetRateFinderData(state) {
            state.locationData = [
                {
                    ...initialLocationData,
                },
            ];
            state.coreTaxes = [];
            state.insClassIds = [];
            state.selectedClientLobs = [];
            state.selectedSubLobs = [];
        },

        setInsurerStatus(state, action: PayloadAction<{ checked: boolean; index: number; name: keyof InsurerStatus }>) {
            state.locationData[action.payload.index].insurerStatus[action.payload.name] = action.payload.checked;
        },

        setVariations(
            state,
            action: PayloadAction<{
                index: number;
                varId: string;
                classId: string;
                className: string;
                variationName: string;
                variations: IClassVariation[];
            }>,
        ) {
            const copyVariations = state.locationData[action.payload.index].selectedVariations;
            const varIndex = copyVariations.findIndex(
                (variation) =>
                    variation.variationId === action.payload.varId && variation.insClassId === action.payload.classId,
            );
            const copySelectAllIndexes = _.cloneDeep(state.locationData[action.payload.index].selectAllIndex);
            const insClassIndex = copySelectAllIndexes.indexOf(action.payload.classId);
            if (varIndex === -1) {
                copyVariations.push({
                    variationId: action.payload.varId,
                    variationName: action.payload.variationName,
                    insClassId: action.payload.classId,
                    insClassName: action.payload.className,
                });
                const currentItemVariations = copyVariations.filter(
                    (item) => item.insClassId === action.payload.classId,
                );
                if (currentItemVariations.length === action.payload.variations[0]?.insVariations.length) {
                    copySelectAllIndexes.push(action.payload.classId);
                }
            } else {
                copyVariations.splice(varIndex, 1);
                if (insClassIndex !== -1) copySelectAllIndexes.splice(insClassIndex, 1);
            }
            state.locationData[action.payload.index].selectedVariations = copyVariations;
            state.locationData[action.payload.index].selectAllIndex = copySelectAllIndexes;
            state.coreTaxes = [];
        },

        toggleVariations(
            state,
            action: PayloadAction<{
                index: number;
                variationIds: ISelectedVariations[];
                variations: IClassVariation[];
            }>,
        ) {
            const copySelectAllIndexes = _.cloneDeep(state.locationData[action.payload.index].selectAllIndex);
            const insClassIndex = copySelectAllIndexes.indexOf(action.payload.variations[0].insClassId);
            if (insClassIndex === -1) {
                copySelectAllIndexes.push(action.payload.variations[0].insClassId);
            } else {
                copySelectAllIndexes.splice(insClassIndex, 1);
            }

            // state.locationData[action.payload.index].selectedVariations = action.payload.variationIds;
            state.locationData[action.payload.index].selectAllIndex = copySelectAllIndexes;
            state.coreTaxes = [];
        },
        resetCoreTaxes(state) {
            state.coreTaxes = [];
        },
        removeVariations(state, action: PayloadAction<string[]>) {
            let copyLocationData = _.cloneDeep(state.locationData);

            copyLocationData = copyLocationData.map((item) => {
                const filteredVariations = item.selectedVariations.filter((variation) =>
                    action.payload.includes(variation.insClassId),
                );
                const filteredSelectAllIndex = item.selectAllIndex.filter((index) => action.payload.includes(index));
                return { ...item, selectedVariations: filteredVariations, selectAllIndex: filteredSelectAllIndex };
            });

            state.locationData = copyLocationData;
        },
        setInsClassIds(state, action: PayloadAction<string[]>) {
            state.insClassIds = action.payload;
        },
        setAccordianOpen(state, action: PayloadAction<{ index: number; isOpen: boolean }>) {
            state.locationData[action.payload.index].isOpen = !action.payload.isOpen;
        },
        setLocationFromRiskLocator(state, action: PayloadAction<ILocationData[]>) {
            state.locationData = action.payload;
        },
        setLocationFromRateFinder(state, action: PayloadAction<ILocationData[]>) {
            state.locationData = action.payload;
        },
        setSelectedClientLobs(state, action: PayloadAction<string[]>) {
            const copyLocationData = _.cloneDeep(state.locationData);
            state.selectedSubLobs = state.selectedSubLobs.filter((lob: ClientSubLobs) =>
                action.payload.includes(lob.clientLob),
            );

            copyLocationData.forEach((location) => {
                location.lobVariations.forEach((lob) => {
                    if (!action.payload.includes(lob.lobName)) {
                        lob.variations?.forEach((item) => {
                            item.variations?.forEach((variation) => {
                                variation.isSelected = false;
                            });
                        });
                    }
                });
            });
            state.locationData = copyLocationData;

            state.selectedClientLobs = action.payload;
        },
        setSelectedSubLobs(state, action: PayloadAction<ClientSubLobs>) {
            const copyLocationData = _.cloneDeep(state.locationData);
            let copySelectedClientSubLobs = _.cloneDeep(state.selectedSubLobs);
            copySelectedClientSubLobs = copySelectedClientSubLobs.filter(
                (lob: ClientSubLobs) => lob.clientLob !== action.payload.clientLob,
            );
            copySelectedClientSubLobs.push(action.payload);
            state.selectedSubLobs = copySelectedClientSubLobs;

            const allSubLobs = _.flatMap(copySelectedClientSubLobs, 'subLobs');

            copyLocationData.forEach((location) => {
                location.lobVariations.forEach((lob) => {
                    if (!allSubLobs.includes(lob.subLobName)) {
                        location.selectAllLobIds = location.selectAllLobIds.filter((id) => id !== lob.lobMasterId);
                        lob.variations?.forEach((variation) => {
                            variation.variations.forEach((item) => {
                                item.isSelected = false;
                            });
                        });
                    }
                });
            });
            state.locationData = copyLocationData;
        },

        toggleLobVariations(state, action: PayloadAction<{ locIndex: number; varIndex: number; lobMasterId: string }>) {
            const { locIndex: index, varIndex, lobMasterId } = action.payload;
            const copyLocationData = _.cloneDeep(state.locationData);
            const location = copyLocationData[index];

            const lobVariation = location.lobVariations.find((lob) => lob.lobMasterId === lobMasterId);

            if (lobVariation) {
                lobVariation.variations?.forEach((variation, idx) => {
                    if (idx === varIndex) {
                        variation.variations.forEach((item) => {
                            item.isSelected = !item.isSelected;
                        });
                    }
                });

                const isAllItemsSelected = lobVariation.variations?.every((variation) =>
                    variation.variations.every((item) => item.isSelected),
                );

                const selectAllLobIds = location.selectAllLobIds;
                const currentIndex = selectAllLobIds.indexOf(lobMasterId);

                if (isAllItemsSelected && currentIndex === -1) {
                    selectAllLobIds.push(lobMasterId); // Add if all variations selected
                } else if (!isAllItemsSelected && currentIndex !== -1) {
                    selectAllLobIds.splice(currentIndex, 1); // Remove if not all selected
                }
            }
            state.coreTaxes = [];
            state.locationData = copyLocationData;
        },

        toggleSelectAllLobIds(state, action: PayloadAction<{ locIndex: number; lobMasterId: string }>) {
            const { locIndex: index, lobMasterId } = action.payload;
            const location = _.cloneDeep(state.locationData[index]);
            const isSelected = location.selectAllLobIds.includes(lobMasterId);

            if (isSelected) {
                location.selectAllLobIds = location.selectAllLobIds.filter((id) => id !== lobMasterId);
            } else {
                location.selectAllLobIds.push(lobMasterId);
            }

            location.lobVariations.forEach((lobVariation: IClientLob) => {
                if (lobVariation.lobMasterId === lobMasterId) {
                    lobVariation.variations?.forEach((variation) => {
                        variation.variations.forEach((item) => {
                            item.isSelected = !isSelected;
                        });
                    });
                }
            });
            state.locationData[index] = location;
        },

        setLocationData(state, action: PayloadAction<ILocationData[]>) {
            state.locationData = action.payload;
        },
        updateTaxScheduleLocationDetailsId(state, action: PayloadAction<{ index: number; code: string }>) {
            if (state.locationData[action.payload.index]) {
                state.locationData[action.payload.index].scheduleLocationDetailsId = action.payload.code;
            }
        },
        updateLocationDataIsDelete(state, action: PayloadAction<{ index: number; isDeleted: boolean }>) {
            if (state.locationData[action.payload.index]) {
                state.locationData[action.payload.index].isDeleted = action.payload.isDeleted;
            }
        },
        updateLocalPolicyLocation(state, action: PayloadAction<{ index: number; isDeleted: boolean }>) {
            if (state.localPolicyLocations[action.payload.index]) {
                state.localPolicyLocations[action.payload.index].isDeleted = action.payload.isDeleted;
            }
        },
        setEffectiveDate(state, action: PayloadAction<Date | undefined>) {
            state.effectiveDate = action.payload;
        },
    },
});

export const {
    addLocationData,
    addLocalPolicyLocationData,
    addLocationDataAddCountry,
    removeLastLocationData,
    removeLocalPolicyLastLocationData,
    removeLocationData,
    setNewlyAddedRiskLocationCountry,
    setisGermanEEA,
    setisGermanLocalPolicy,
    setSelectedCountry,
    setSelectedRegion,
    setSelectedSubRegion,
    setLocalPolicySelectedSubRegion,
    setLocalPolicySelectedRegion,
    setLocalPolicySelectedCountry,
    resetRateFinderData,
    setInsurerStatus,
    setVariations,
    toggleVariations,
    resetCoreTaxes,
    removeVariations,
    resetLocalPolicyLocationData,
    setInsClassIds,
    setAccordianOpen,
    setLocationFromRiskLocator,
    setLocationFromRateFinder,
    setSelectedClientLobs,
    setSelectedSubLobs,
    toggleLobVariations,
    toggleSelectAllLobIds,
    setLocationData,
    updateTaxScheduleLocationDetailsId,
    updateLocationDataIsDelete,
    updateLocalPolicyLocation,
    setEffectiveDate,
} = rateFinderSlice.actions;

export default rateFinderSlice.reducer;
